import React, { useEffect, useRef } from 'react';
import { useAuth } from '@platform/core/src/context/AuthContext';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useUI } from '@platform/core/src/context/UIContext';
import { getMyFitnessProfile } from '../services/profileApi';
import { getLegalComplianceStatus } from '@platform/core/src/services/legalApi';
import { onboardingPreChatConfig } from '../config/app/sessionConfig';
import { getStartupData } from '../services/startupService';

export const useOnboardingLock = () => {
  const { isAuthenticated } = useAuth();
  const { teamName, initializeSession } = useSession();
  const { setNavigationLock, isNavigationLocked, navigationLockReason } = useUI();
  const redirectingRef = useRef(false);
  const dataCacheRef = useRef({ legalStatus: null, profile: null });

  useEffect(() => {
    redirectingRef.current = false;
    // Clear cache when switching teams to ensure fresh checks
    dataCacheRef.current = { legalStatus: null, profile: null };
  }, [teamName]);

  useEffect(() => {
    if (!isAuthenticated) {
        dataCacheRef.current = { legalStatus: null, profile: null };
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const enforceLock = async () => {
      try {
        let { legalStatus, profile } = dataCacheRef.current;

        // CRITICAL FIX:
        // If navigation is currently locked, we CANNOT trust the cache.
        // We must force a re-fetch to see if the user has resolved the issue.
        if (isNavigationLocked) {
            legalStatus = null;
        }

        // 1. Try loading from startup service (fast cache) if we don't have data
        if (!legalStatus || !profile) {
            try {
                const startupData = await getStartupData();
                if (startupData) {
                    // Only use startup data if we aren't locked, or if it's brand new
                    if (!isNavigationLocked) {
                        legalStatus = startupData.legalData;
                        profile = startupData.profileData;
                    }
                }
            } catch (e) {
                console.warn("[useOnboardingLock] Startup data load error", e);
            }
        }

        // 2. If still missing (or invalidated because we are locked), fetch fresh from API
        if (!legalStatus || !profile) {
             const [l, p] = await Promise.all([
                 getLegalComplianceStatus('workout_smart'),
                 getMyFitnessProfile()
             ]);
             legalStatus = l;
             profile = p;
        }

        // Update cache for next run
        dataCacheRef.current = { legalStatus, profile };

        // --- CHECK 1: PROFILE ---
        if (!profile || !profile.onboarding_completed_at) {
           const lockMsg = "Complete setup to unlock features.";
           if (!isNavigationLocked || navigationLockReason !== lockMsg) {
               console.log("[useOnboardingLock] Onboarding incomplete. Locking navigation.");
               setNavigationLock(true, lockMsg);
           }
           
           if (teamName !== 'onboarding_team' && !redirectingRef.current) {
              console.log("[useOnboardingLock] Interrupting session -> Redirecting to onboarding_team.");
              redirectingRef.current = true;
              await initializeSession(null, {
                team_name: 'onboarding_team',
                preChatConfig: onboardingPreChatConfig
              });
           }
           return;
        }

        // --- CHECK 2: LEGAL AGREEMENTS ---
        if (!legalStatus.all_accepted) {
          const lockMsg = "Please review and sign required agreements to continue.";
          if (!isNavigationLocked || navigationLockReason !== lockMsg) {
              console.log("[useOnboardingLock] Agreements pending. Locking navigation.");
              setNavigationLock(true, lockMsg);
          }

          if (teamName !== 'legal_team' && !redirectingRef.current) {
             console.log("[useOnboardingLock] Interrupting session -> Redirecting to legal_team.");
             redirectingRef.current = true;
             await initializeSession(null, {
               team_name: 'legal_team',
               preChatConfig: []
             });
          }
          return;
        }

        // --- UNLOCK ---
        // If we reach here, both checks passed. If we were locked, UNLOCK NOW.
        if (isNavigationLocked) {
             console.log("[useOnboardingLock] User is compliant. Unlocking navigation.");
             setNavigationLock(false);
             
             // If we were stuck in a restriction team, move to welcome
             if (teamName === 'onboarding_team' || teamName === 'legal_team') {
                 await initializeSession(null, {
                     team_name: 'welcome_group'
                 });
             }
        }
      } catch (error) {
        console.error("[useOnboardingLock] Error checking onboarding status:", error);
      }
    };
    enforceLock();
  }, [isAuthenticated, teamName, setNavigationLock, isNavigationLocked, navigationLockReason, initializeSession]);
};

export const useOnboardingRedirect = useOnboardingLock;